import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth.store';

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
  withCredentials: true, // send HttpOnly cookies on every request
});

/**
 * Timeout for requests that call the AI provider. The 15s default is far too
 * short for these: drafting a report summary takes ~30s and reviewing a
 * requirement with attached evidence has been measured at ~15s, right on the
 * edge. A client-side abort does not stop the server call — it completes and
 * is billed — so an under-set timeout throws away work that has been paid for.
 * Pass as `{ timeout: AI_REQUEST_TIMEOUT_MS }` on any /ai/* request.
 */
export const AI_REQUEST_TIMEOUT_MS = 180_000;

// Attach token to every request; strip Content-Type for FormData so the
// browser/Axios can set multipart/form-data with the correct boundary.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // When sending FormData, remove the instance-level application/json
  // default so Axios/the browser can set the correct multipart boundary.
  if (config.data instanceof FormData) {
    // AxiosHeaders supports delete() in v1.x; fall back to assignment
    if (typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else {
      delete (config.headers as Record<string, unknown>)['Content-Type'];
    }
  }
  return config;
});

/**
 * The endpoints that *are* the sign-in.
 *
 * A 401 from one of these means the credential was wrong, not that a
 * session expired — so the refresh-and-bounce below is not just useless
 * there, it is harmful: it reloads the page and destroys the explanation
 * before anyone reads it. A mistyped password used to blank the login form
 * with no message at all, and a spent SSO code did the same.
 */
const SIGN_IN_PATHS = ['/auth/login', '/auth/refresh', '/auth/sso/exchange'];
const isSignInRequest = (url?: string) => !!url && SIGN_IN_PATHS.some(p => url.includes(p));

// Refresh token on 401
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    // The policy requires a second factor this session does not have. Every
    // route except profile/MFA answers 403 with this code; send them there
    // once rather than letting each page show its own error.
    const code = (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
    if (error.response?.status === 403 && code === 'MFA_SETUP_REQUIRED' && !window.location.pathname.startsWith('/profile')) {
      window.location.assign('/profile?mfa=required');
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !original._retry && !isSignInRequest(original?.url)) {
      original._retry = true;
      try {
        // Refresh token is in HttpOnly cookie — sent automatically
        const { data } = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true });
        // If the session was terminated (idle logout or explicit logout) while
        // this request was in flight, do NOT resurrect it — the store token is
        // null only after a logout, whereas a normal 401 still holds the old
        // (expired) access token. This prevents an idle logout from being undone
        // by a concurrent successful refresh.
        if (useAuthStore.getState().token === null) {
          return Promise.reject(error);
        }
        useAuthStore.getState().setToken(data.data.accessToken);
        original.headers.Authorization = `Bearer ${data.data.accessToken}`;
        return api(original);
      } catch {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

// Convenience helpers
export function getApiError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { error?: { message?: string } })?.error?.message ?? err.message;
  }
  return 'An unexpected error occurred';
}

/** Extract per-field validation error arrays from an API VALIDATION_ERROR response */
export function getApiFieldErrors(err: unknown): Record<string, string[]> | null {
  if (!axios.isAxiosError(err)) return null;
  const body = err.response?.data as {
    error?: { code?: string; details?: Record<string, string[]> };
  } | undefined;
  if (body?.error?.code === 'VALIDATION_ERROR' && body.error.details) {
    return body.error.details;
  }
  return null;
}
